import { type ComponentProps, forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ArrowControl,
  CarouselArrowControlContainer
} from '@elfsight/lib-carousel';
import {
  SnapCarousel as LibSnapCarousel,
  type SnapCarouselAPI
} from '@elfsight/lib-snap-carousel';
import { createReadableClass, isMobile } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useLayout } from '../layouts/layout-context';

type SnapCarouselProps = ComponentProps<typeof LibSnapCarousel> & {
  arrowControlMargin?: number | string;
  arrowControlMarginLeft?: number | string;
  arrowControlMarginRight?: number | string;
  withShadow?: boolean;
};

export const SnapCarousel = forwardRef<SnapCarouselAPI, SnapCarouselProps>(
  (
    {
      arrowControlMargin,
      arrowControlMarginLeft,
      arrowControlMarginRight,
      withShadow = false,
      ...props
    },
    ref
  ) => {
    const {
      carouselArrowBackgroundColor,
      carouselArrowBackgroundColorOnHover,
      carouselArrowColor,
      carouselArrowColorOnHover,
      carouselArrowSize,
      _forceShowArrows
    } = useSettings();
    const {
      breakpoints: { isMediumBreakpoint },
      snapCarouselMargins
    } = useLayout();

    const withArrows = !isMediumBreakpoint || !isMobile() || !!_forceShowArrows;

    const { left: snapCarouselMarginLeft, right: snapCarouselMarginRight } =
      (() => {
        const { left: snapCarouselMarginLeft, right: snapCarouselMarginRight } =
          snapCarouselMargins;
        if (!withArrows) {
          return {};
        }

        return {
          left:
            isMediumBreakpoint && snapCarouselMarginLeft
              ? -1 * snapCarouselMarginLeft
              : arrowControlMarginLeft,
          right:
            isMediumBreakpoint && snapCarouselMarginRight
              ? -1 * snapCarouselMarginRight
              : arrowControlMarginRight
        };
      })();

    const derivedWithShadow = withShadow && !withArrows;

    const [withShadowLeft, setWithShadowLeft] = useState(false);
    const [withShadowRight, setWithShadowRight] = useState(false);

    const handleUpdateScrollState = (
      isScrollStart: boolean,
      isScrollEnd: boolean
    ) => {
      setWithShadowLeft(derivedWithShadow && !isScrollStart);
      setWithShadowRight(derivedWithShadow && !isScrollEnd);
    };

    return (
      <StyledSnapCarousel
        // @ts-expect-error Похоже на какую-то проблему в типах SC, т.к. если использовать LibSnapCarousel, то TS не ругается
        ref={ref}
        withArrows={withArrows}
        onUpdateScrollState={handleUpdateScrollState}
        arrowControl={({ direction, onClick }) => (
          <StyledCarouselArrowControlContainer
            aria-label={direction === 'left' ? 'Previous' : 'Next'}
            role="button"
            tabIndex={0}
            $direction={direction}
            $margin={arrowControlMargin}
            $marginLeft={snapCarouselMarginLeft}
            $marginRight={snapCarouselMarginRight}
            $size={carouselArrowSize}
            _floating
          >
            <ArrowControl
              className={createReadableClass({
                component: 'snap-carousel',
                element: 'arrow-control',
                index: direction
              })}
              backgroundColor={carouselArrowBackgroundColor}
              backgroundColorOnHover={carouselArrowBackgroundColorOnHover}
              direction={direction}
              iconColor={carouselArrowColor}
              iconColorOnHover={carouselArrowColorOnHover}
              size={carouselArrowSize}
              onClick={onClick}
            />
          </StyledCarouselArrowControlContainer>
        )}
        $shadowLeft={withShadowLeft}
        $shadowRight={withShadowRight}
        {...props}
      />
    );
  }
);

const StyledCarouselArrowControlContainer = styled(
  CarouselArrowControlContainer
).attrs<{
  $direction: 'left' | 'right';
  $size: number;
  $margin?: number | string;
  $marginLeft?: number | string;
  $marginRight?: number | string;
  $shadowLeft?: boolean;
  $shadowRight?: boolean;
}>(
  ({
    $direction,
    $margin = 0,
    $marginLeft = $margin,
    $marginRight = $margin,
    $size = 0
  }) => ({
    style:
      $direction === 'left'
        ? {
            left:
              typeof $marginLeft === 'string'
                ? $marginLeft
                : `${-1 * ($size / 2 + $marginLeft)}px`
          }
        : {
            right:
              typeof $marginRight === 'string'
                ? $marginRight
                : `${-1 * ($size / 2 + $marginRight)}px`
          },
    className: createReadableClass({
      component: 'snap-carousel',
      element: 'arrow-control-container'
    })
  })
)``;

const StyledSnapCarousel = styled(LibSnapCarousel)<{
  $shadowLeft?: boolean;
  $shadowRight?: boolean;
}>`
  position: static;

  ${({ $shadowLeft }) =>
    $shadowLeft &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 8px;
        height: 100%;
        z-index: 99999;
        background: linear-gradient(
          90deg,
          rgba(17, 17, 17, 0.1) 0%,
          rgba(17, 17, 17, 0) 100%
        );
      }
    `}

  ${({ $shadowRight }) =>
    $shadowRight &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 8px;
        height: 100%;
        z-index: 99999;
        background: linear-gradient(
          -90deg,
          rgba(17, 17, 17, 0.1) 0%,
          rgba(17, 17, 17, 0) 100%
        );
      }
    `}
`;
